/* ===================================================================
   ReplyLibrary -- the owner approves the twenty reply lines.
   Built 2026-08-16 (Steve: "the screen that we need to approve or have
   the client approved for the twenty lines needs to be built").

   WHAT THE OWNER DOES HERE
   Two banks of ten. They read each line, tap Approve on the ones they
   like, edit or skip the rest, and can add their own. Then, if they want,
   they flip auto-replies on for 4-5 star reviews only.

   RULES THIS SCREEN ENFORCES IN THE UI, NOT JUST THE API
   * Auto-reply is OFF by default and the switch cannot be turned on until
     at least one 4-5 star line is approved. A switch that appears to work
     but silently does nothing is worse than one that explains itself.
   * The 3-and-below bank NEVER gets an auto-post affordance. Its lines
     pre-fill a draft and a person always approves. The copy says so
     plainly, right on the bank, so nobody assumes otherwise.
   * Nothing is usable until approved, so unapproved lines read as inert.

   ACCESSIBILITY (feedback_rhody_reviews_app_accessibility_ui): the ICP is
   an older tradesman on a glary phone. Big tap targets, high contrast,
   real labels, no placeholder-as-label, and state shown with words and not
   colour alone.
   =================================================================== */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  replyTemplatesApi,
  type ReplyBand,
  type ReplyTemplate,
  type ReplyTemplatesResponse,
} from '../lib/api/reply_templates';

type Busy = Record<string, boolean>;

export default function ReplyLibrary() {
  const [data, setData] = useState<ReplyTemplatesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState<ReplyBand | null>(null);
  const [newBody, setNewBody] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const d = await replyTemplatesApi.list(signal);
      // First visit: nobody has seeded the library yet. Do it silently so the
      // owner lands on twenty real lines rather than an empty screen.
      if (!d.positive.length && !d.negative.length) {
        await replyTemplatesApi.seed();
        setData(await replyTemplatesApi.list(signal));
      } else {
        setData(d);
      }
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your replies.');
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const mark = (id: string, on: boolean) =>
    setBusy((b) => ({ ...b, [id]: on }));

  async function patch(id: string, input: Parameters<typeof replyTemplatesApi.update>[1]) {
    mark(id, true);
    try {
      await replyTemplatesApi.update(id, input);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That change did not save.');
    } finally {
      mark(id, false);
    }
  }

  async function approveAll(band: ReplyBand) {
    const rows = (band === 'positive' ? data?.positive : data?.negative) || [];
    const pending = rows.filter((r) => !r.approved && r.active);
    for (const r of pending) await replyTemplatesApi.update(r.id, { approved: true });
    await load();
    setNotice(`Approved ${pending.length} ${pending.length === 1 ? 'reply' : 'replies'}.`);
  }

  async function toggleAuto(next: boolean) {
    mark('auto', true);
    setError(null);
    try {
      await replyTemplatesApi.setAutoReply(next);
      await load();
      setNotice(next
        ? 'Auto-replies are on for 4 and 5 star reviews.'
        : 'Auto-replies are off. Every reply will wait for you.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that setting.');
    } finally {
      mark('auto', false);
    }
  }

  async function addOwn(band: ReplyBand) {
    const body = newBody.trim();
    if (!body) return;
    mark('new', true);
    try {
      await replyTemplatesApi.create({ band, body });
      setNewBody('');
      setAdding(null);
      await load();
      setNotice('Your reply was added and is ready to use.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that reply.');
    } finally {
      mark('new', false);
    }
  }

  const canAuto = (data?.approved_positive_count || 0) > 0;

  if (error && !data) {
    return (
      <main className="rl-wrap">
        <p className="rl-error" role="alert">{error}</p>
      </main>
    );
  }
  if (!data) return <main className="rl-wrap"><p>Loading your replies...</p></main>;

  return (
    <main className="rl-wrap">
      <header className="rl-head">
        <h1>Your replies</h1>
        <p className="rl-lede">
          These are the replies we will use when a review comes in. Read them, approve
          the ones you like, change any you would say differently, and add your own if
          you want. We will not use a reply you have not approved.
        </p>
      </header>

      {notice && <p className="rl-notice" role="status">{notice}</p>}
      {error && <p className="rl-error" role="alert">{error}</p>}

      {/* ---- The auto-reply switch. Off by default, and gated. ---- */}
      <section className="rl-auto" aria-labelledby="rl-auto-h">
        <h2 id="rl-auto-h">Answer good reviews automatically</h2>
        <p>
          If you turn this on, we will post one of your approved replies to
          <strong> 4 and 5 star reviews</strong> on their own, so you do not have to
          touch them. Anything <strong>3 stars or below always waits for you</strong>,
          no matter what this is set to.
        </p>
        <div className="rl-switch-row">
          <button
            type="button"
            className={`rl-switch ${data.auto_reply_enabled ? 'is-on' : ''}`}
            role="switch"
            aria-checked={data.auto_reply_enabled}
            disabled={busy.auto || (!data.auto_reply_enabled && !canAuto)}
            onClick={() => void toggleAuto(!data.auto_reply_enabled)}
          >
            <span className="rl-switch-track" aria-hidden="true"><span className="rl-knob" /></span>
            <span className="rl-switch-label">
              {data.auto_reply_enabled ? 'On' : 'Off'}
            </span>
          </button>
          {!canAuto && !data.auto_reply_enabled && (
            <span className="rl-hint">
              Approve at least one reply for 4 and 5 star reviews first.
            </span>
          )}
        </div>
      </section>

      <Bank
        band="positive"
        title="For 4 and 5 star reviews"
        blurb="Happy customers. These are the ones that can go out on their own if you switch that on above."
        rows={data.positive}
        busy={busy}
        editing={editing}
        draft={draft}
        adding={adding === 'positive'}
        newBody={newBody}
        onEdit={(id, body) => { setEditing(id); setDraft(body); }}
        onCancelEdit={() => setEditing(null)}
        onDraft={setDraft}
        onSave={(id) => { void patch(id, { body: draft }); setEditing(null); }}
        onApprove={(id, v) => void patch(id, { approved: v })}
        onActive={(id, v) => void patch(id, { active: v })}
        onApproveAll={() => void approveAll('positive')}
        onStartAdd={() => { setAdding('positive'); setNewBody(''); }}
        onCancelAdd={() => setAdding(null)}
        onNewBody={setNewBody}
        onAdd={() => void addOwn('positive')}
      />

      <Bank
        band="negative"
        title="For 3 star reviews and below"
        blurb="These never post on their own. We fill in the reply for you and it waits until you approve it."
        rows={data.negative}
        busy={busy}
        editing={editing}
        draft={draft}
        adding={adding === 'negative'}
        newBody={newBody}
        onEdit={(id, body) => { setEditing(id); setDraft(body); }}
        onCancelEdit={() => setEditing(null)}
        onDraft={setDraft}
        onSave={(id) => { void patch(id, { body: draft }); setEditing(null); }}
        onApprove={(id, v) => void patch(id, { approved: v })}
        onActive={(id, v) => void patch(id, { active: v })}
        onApproveAll={() => void approveAll('negative')}
        onStartAdd={() => { setAdding('negative'); setNewBody(''); }}
        onCancelAdd={() => setAdding(null)}
        onNewBody={setNewBody}
        onAdd={() => void addOwn('negative')}
      />
    </main>
  );
}

function Bank(props: {
  band: ReplyBand;
  title: string;
  blurb: string;
  rows: ReplyTemplate[];
  busy: Busy;
  editing: string | null;
  draft: string;
  adding: boolean;
  newBody: string;
  onEdit: (id: string, body: string) => void;
  onCancelEdit: () => void;
  onDraft: (v: string) => void;
  onSave: (id: string) => void;
  onApprove: (id: string, v: boolean) => void;
  onActive: (id: string, v: boolean) => void;
  onApproveAll: () => void;
  onStartAdd: () => void;
  onCancelAdd: () => void;
  onNewBody: (v: string) => void;
  onAdd: () => void;
}) {
  const { rows } = props;
  const approved = useMemo(() => rows.filter((r) => r.approved && r.active).length, [rows]);
  const pending = rows.filter((r) => !r.approved && r.active).length;

  return (
    <section className="rl-bank" aria-labelledby={`rl-${props.band}`}>
      <div className="rl-bank-head">
        <h2 id={`rl-${props.band}`}>{props.title}</h2>
        <p>{props.blurb}</p>
        <p className="rl-count">
          <strong>{approved}</strong> approved{pending > 0 ? `, ${pending} still to look at` : ''}
        </p>
        {pending > 0 && (
          <button type="button" className="rl-btn rl-btn-ghost" onClick={props.onApproveAll}>
            Approve all {pending}
          </button>
        )}
      </div>

      <ul className="rl-list">
        {rows.map((r) => (
          <li key={r.id} className={`rl-item ${r.approved ? 'is-approved' : ''} ${r.active ? '' : 'is-off'}`}>
            {props.editing === r.id ? (
              <>
                <label className="rl-label" htmlFor={`edit-${r.id}`}>Change this reply</label>
                <textarea
                  id={`edit-${r.id}`}
                  className="rl-textarea"
                  value={props.draft}
                  rows={3}
                  onChange={(e) => props.onDraft(e.target.value)}
                />
                <div className="rl-actions">
                  <button type="button" className="rl-btn" onClick={() => props.onSave(r.id)}>Save</button>
                  <button type="button" className="rl-btn rl-btn-ghost" onClick={props.onCancelEdit}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <p className="rl-body">{r.body}</p>
                <p className="rl-state">
                  {r.approved ? 'Approved. We can use this.' : 'Not approved yet. We will not use this.'}
                  {r.source === 'client' ? ' Yours.' : ''}
                </p>
                <div className="rl-actions">
                  <button
                    type="button"
                    className={`rl-btn ${r.approved ? 'rl-btn-ghost' : ''}`}
                    disabled={props.busy[r.id]}
                    onClick={() => props.onApprove(r.id, !r.approved)}
                  >
                    {r.approved ? 'Unapprove' : 'Approve'}
                  </button>
                  <button type="button" className="rl-btn rl-btn-ghost"
                    onClick={() => props.onEdit(r.id, r.body)}>Change wording</button>
                  <button type="button" className="rl-btn rl-btn-ghost"
                    disabled={props.busy[r.id]}
                    onClick={() => props.onActive(r.id, !r.active)}>
                    {r.active ? 'Do not use' : 'Use again'}
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>

      {props.adding ? (
        <div className="rl-add">
          <label className="rl-label" htmlFor={`new-${props.band}`}>Write your own reply</label>
          <textarea
            id={`new-${props.band}`}
            className="rl-textarea"
            rows={3}
            value={props.newBody}
            onChange={(e) => props.onNewBody(e.target.value)}
          />
          <p className="rl-hint">
            Type {'{{name}}'} where you want the customer's first name. If we do not have
            their name, we leave it out and the sentence still reads right.
          </p>
          <div className="rl-actions">
            <button type="button" className="rl-btn" onClick={props.onAdd}>Add this reply</button>
            <button type="button" className="rl-btn rl-btn-ghost" onClick={props.onCancelAdd}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" className="rl-btn rl-btn-ghost rl-add-btn" onClick={props.onStartAdd}>
          Add my own reply
        </button>
      )}
    </section>
  );
}
